import { Box, Stack, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import TachometerGraph from '../../../Elements/Graphs/TachometerGraph'
import { SelectedValue, TachoGraphVersion, Unit } from '../../../Elements/SystemSettings/SystemSettingsUtils'
import { Position } from 'src/Types/ui'
import { UnitType } from 'src/Libs/types'
import { escape } from 'lodash'
import GraphTagList from 'src/Libs/graph-cards/components/GraphTagList'
import { useGraphicBaseCard } from 'src/Libs/graph-cards'

const useStyles = makeStyles()(theme => ({
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '200px',
    height: '100%',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    background: theme.palette.grey.grey07,
    padding: theme.spacing(4),
    position: 'relative',
    border: `1px solid ${theme.palette.shades.shade03}`,
  },
  graphContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '75%',
    overflow: 'visible',
    paddingBottom: theme.spacing(3),
  },
  unitContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '25px',
    height: '20px',
    background: theme.palette.grey.grey07,
    boxShadow: 'inset 1px 2px 3px #E6E6E6',
  },
  minMaxValues: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
}))

interface GraphicBaseCardProps {
  title: string
  titlePosition?: Position
  values: number[]
  min: number
  max: number
  unit: Unit
  tagLabel?: (value: number, index: number, unit: UnitType) => string | React.ReactNode
  subtitle?: string
  /** Values for the pointers, if they differ from the tag values (e.g. absolute pointer, signed tag). Defaults to `values`. */
  pointerValues?: number[]
}

const GraphicBaseCard: React.FC<GraphicBaseCardProps> = ({
  title,
  min,
  max,
  values,
  unit,
  tagLabel,
  titlePosition = 'top',
  subtitle,
  pointerValues,
}) => {
  const { classes } = useStyles()
  const [selectedValues, setSelectedValues] = useState<SelectedValue[]>([])
  const { getGraphValueBackgroundColor } = useGraphicBaseCard()

  // a range without extent cannot be drawn (the pointer angle would be NaN), so pointers and bounds are hidden
  const hasValidRange = max > min

  const graphValues = useMemo<number[]>(() => {
    if (!hasValidRange) return []
    return (pointerValues ?? values).map(value => {
      if (value < min) return min
      if (value > max) return max
      return value
    })
  }, [hasValidRange, pointerValues, values, min, max])

  const updateSelectedValues = useCallback(
    (graphValues: number[]): void => {
      const doOverwriteSelection = selectedValues.length !== graphValues.length
      const selected = graphValues.reduce((prev: SelectedValue[], curr, currIdx) => {
        return [...prev, { index: currIdx, isSelected: doOverwriteSelection ? true : Boolean(selectedValues[currIdx]?.isSelected) }]
      }, [])
      setSelectedValues(selected)
    },
    [selectedValues]
  )

  const getGraphColors = useCallback(
    (idx: number, version: TachoGraphVersion): [string, string] => {
      return getGraphValueBackgroundColor(idx, version, values)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values]
  )

  const onClickTab = useCallback(
    (idx: number): void => {
      const updatedValues = selectedValues.map(value => {
        if (value.index === idx) {
          return { index: idx, isSelected: !value.isSelected }
        } else {
          return value
        }
      })
      setSelectedValues(updatedValues)
    },
    [selectedValues]
  )

  useEffect(() => {
    updateSelectedValues(values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  const titleContent = (
    <Stack alignItems={'center'} mb={2}>
      <Typography variant={'h3'}>{title}</Typography>
      {subtitle && <Typography variant={'subtitle2'}>{subtitle}</Typography>}
    </Stack>
  )

  return (
    <Stack className={classes.cardContainer} data-qa={`graphic-base-card-${escape(title.toLowerCase())}`}>
      {titlePosition === 'top' && titleContent}
      {/** Graph */}
      <Box className={classes.graphContainer}>
        <TachometerGraph values={graphValues} min={min} max={max} setGraphValueColor={getGraphColors} selectedValues={selectedValues} />
        {hasValidRange && (
          <Box className={classes.minMaxValues}>
            <Typography variant={'body2'}>{min}</Typography>
            <Typography variant={'body2'}>{max}</Typography>
          </Box>
        )}
        <Box className={classes.unitContainer}>
          <Typography variant={'body2'}>{unit}</Typography>
        </Box>
      </Box>

      {/** Numbers */}
      <GraphTagList sourceValues={values} selectedValues={selectedValues} tagUnit={unit} onClickTag={onClickTab} tagLabel={tagLabel} />

      {/** Title */}
      {titlePosition === 'bottom' && titleContent}
    </Stack>
  )
}

export default GraphicBaseCard
